import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AdminFileBucket, BackendClient } from "@equipped/core/backend/client";
import { lessonFileForManifest, prefixSegments, prefixUpTo, stripPrefix } from "../manifestFile";

/**
 * Browse the gated R2 bucket and pick a file — the docs/168 R2-browse capability,
 * carried over into the redesigned editor (docs/343 v0 §7/§9) as a dialog reachable
 * from a lesson path field's "Browse…" button (`file`, `media`, `narration`,
 * `captions`, or `readalong`). Returns the pack-relative value to store in
 * the lesson (relative to `<packId>/manifest.json`, via the SAME
 * `lessonFileForManifest` helper the old builder used).
 */
export function FilePickerDialog({
  open,
  onClose,
  onPick,
  client,
  token,
  packId,
  bucket = "gated",
}: {
  open: boolean;
  onClose: () => void;
  onPick: (relativeFile: string) => void;
  client: BackendClient | null;
  token: string | null;
  packId: string;
  /** Shared with the File Manager's bucket-aware browser; manifest fields stay gated by default. */
  bucket?: AdminFileBucket;
}) {
  const [prefix, setPrefix] = useState("");

  const listingQuery = useQuery({
    queryKey: ["admin-content-listing", bucket, prefix],
    queryFn: () => client!.adminListContent(token!, prefix, bucket),
    enabled: open && !!client && !!token,
  });
  const listing = listingQuery.data;
  const segs = prefixSegments(prefix);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Browse content</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Button size="small" onClick={() => setPrefix("")}>
            root
          </Button>
          {segs.map((seg, i) => (
            <Box key={`${seg}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography color="text.secondary">›</Typography>
              <Button size="small" onClick={() => setPrefix(prefixUpTo(prefix, i))}>
                {seg}
              </Button>
            </Box>
          ))}
        </Box>
        {listingQuery.isError ? <Alert severity="error">{(listingQuery.error as Error).message}</Alert> : null}
        {listingQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : !listing || (listing.folders.length === 0 && listing.files.length === 0) ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            Nothing here.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {listing.folders.map((folder) => (
              <Box
                key={folder}
                role="button"
                onClick={() => setPrefix(folder)}
                sx={{ py: 1, px: 1, cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}
              >
                📁 {stripPrefix(folder, prefix).replace(/\/$/, "")}
              </Box>
            ))}
            {listing.files.map((file) => (
              <Box key={file.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, px: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {stripPrefix(file.key, prefix)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onPick(lessonFileForManifest(packId, file.key));
                    onClose();
                  }}
                >
                  Pick
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
